import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { useMetricDrilldown, type DrilldownRow } from '@/hooks/useMetricDrilldown';
import type { AnalyticsFilters } from '@/hooks/useAnalyticsFilters';
import type { MetricKey } from '@/lib/analytics/metrics';
import { format } from 'date-fns';

interface AnalyticsDrilldownPanelProps {
  metric: MetricKey | null;
  segment: string | null;
  filters: AnalyticsFilters;
  workspaceId: string;
  fromDate: Date;
  toDate: Date;
  tz: string;
  open: boolean;
  onClose: () => void;
}

const METRIC_TITLES: Record<MetricKey, string> = {
  calls: 'Calls',
  meaningful_calls: 'Meaningful Calls',
  personal_emails: 'Personal Emails',
  campaign_emails: 'Campaign Emails',
  new_contacts: 'New Contacts',
  new_connections: 'New Connections',
  call_outcomes: 'Call Outcomes',
  connections_by_type: 'Connections by Type',
};

export function AnalyticsDrilldownPanel(props: AnalyticsDrilldownPanelProps) {
  const { rows, isLoading, error, hasMore, scrollRef } = useMetricDrilldown({
    workspaceId: props.workspaceId,
    metric: props.metric,
    from: props.fromDate,
    to: props.toDate,
    userFilter: props.filters.user,
    tz: props.tz,
    segment: props.segment,
    enabled: props.open && props.metric !== null,
  });

  return (
    <Sheet
      open={props.open}
      onOpenChange={(isOpen) => {
        if (!isOpen) props.onClose();
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-[600px] flex flex-col">
        <SheetHeader>
          <SheetTitle>
            {props.metric ? METRIC_TITLES[props.metric] : ''}
            {props.segment ? ` — ${props.segment}` : ''}
          </SheetTitle>
        </SheetHeader>
        <div ref={scrollRef} className="flex-1 overflow-y-auto mt-4 space-y-2 pr-1">
          {isLoading && rows.length === 0 && (
            <>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </>
          )}
          {error && (
            <p className="text-destructive text-sm py-4 text-center">
              Couldn't load drilldown
            </p>
          )}
          {!isLoading &&
            !error &&
            rows.length === 0 &&
            props.metric && (
              <p className="text-muted-foreground text-sm py-4 text-center">
                No matching records
              </p>
            )}
          {rows.map((row) => (
            <DrilldownRowItem
              key={row.row_id}
              metric={props.metric!}
              row={row}
            />
          ))}
          {hasMore && (
            <p className="text-center text-sm text-muted-foreground py-2">
              Loading more…
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DrilldownRowItem({
  metric,
  row,
}: {
  metric: MetricKey;
  row: DrilldownRow;
}) {
  const ts = format(new Date(row.row_ts), 'MMM d, yyyy h:mm a');
  const p = row.payload as Record<string, unknown>;

  switch (metric) {
    case 'calls':
    case 'meaningful_calls': {
      const contactId = p.contact_id as string | null;
      const contactName = (p.contact_name as string) || '(no name)';
      const outcome = p.outcome as string | null;
      const meaningful = p.meaningful as boolean | null;
      return (
        <div className="flex items-center justify-between p-2 border rounded text-sm">
          <div className="flex flex-col">
            {contactId ? (
              <Link
                to={`/contacts/${contactId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-medium"
              >
                {contactName}
              </Link>
            ) : (
              <span className="font-medium">{contactName}</span>
            )}
            <span className="text-xs text-muted-foreground">{ts}</span>
          </div>
          <div className="flex items-center gap-2">
            {outcome && (
              <span className="px-2 py-0.5 bg-muted rounded text-xs">{outcome}</span>
            )}
            {meaningful && (
              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs dark:bg-green-900 dark:text-green-200">
                Meaningful
              </span>
            )}
          </div>
        </div>
      );
    }
    case 'personal_emails': {
      const contactId = p.contact_id as string | null;
      const recipient =
        (p.recipient_email as string | null) ||
        (p.recipient_name as string | null) ||
        '(unknown recipient)';
      const subject = p.subject as string | null;
      return (
        <div className="p-2 border rounded text-sm">
          {contactId ? (
            <Link
              to={`/contacts/${contactId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline font-medium"
            >
              {recipient}
            </Link>
          ) : (
            <span className="font-medium">{recipient}</span>
          )}
          <div className="text-xs text-muted-foreground truncate">
            {subject ?? '(no subject)'}
          </div>
          <div className="text-xs text-muted-foreground">{ts}</div>
        </div>
      );
    }
    case 'campaign_emails': {
      const recipient = (p.recipient_email as string | null) ?? '(unknown recipient)';
      const subject = p.subject as string | null;
      const campaignId = p.campaign_id as string | null;
      return (
        <div className="p-2 border rounded text-sm">
          <div className="font-medium">{recipient}</div>
          {campaignId ? (
            <Link
              to={`/inbox/campaigns/${campaignId}`}
              className="text-xs text-muted-foreground hover:underline truncate block"
            >
              {subject ?? '(no subject)'}
            </Link>
          ) : (
            <div className="text-xs text-muted-foreground truncate">
              {subject ?? '(no subject)'}
            </div>
          )}
          <div className="text-xs text-muted-foreground">{ts}</div>
        </div>
      );
    }
    case 'new_contacts': {
      const firstName = p.first_name as string | null;
      const lastName = p.last_name as string | null;
      const name = [firstName, lastName].filter(Boolean).join(' ') || '(no name)';
      return (
        <div className="p-2 border rounded text-sm">
          <Link
            to={`/contacts/${row.row_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline font-medium"
          >
            {name}
          </Link>
          <div className="text-xs text-muted-foreground">{ts}</div>
        </div>
      );
    }
    case 'new_connections': {
      const c1 = p.contact_id_1 as string | null;
      const c2 = p.contact_id_2 as string | null;
      const relType = p.relationship_type as string | null;
      return (
        <div className="p-2 border rounded text-sm">
          <div className="flex items-center gap-1">
            {c1 ? (
              <Link
                to={`/contacts/${c1}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-medium"
              >
                Contact A
              </Link>
            ) : (
              <span>Contact A</span>
            )}
            <span className="text-muted-foreground">→</span>
            {c2 ? (
              <Link
                to={`/contacts/${c2}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-medium"
              >
                Contact B
              </Link>
            ) : (
              <span>Contact B</span>
            )}
            {relType && (
              <span className="text-xs text-muted-foreground ml-2">({relType})</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{ts}</div>
        </div>
      );
    }
    default:
      return null;
  }
}
